import { websocketUrl } from "../../utils/constants";
import { ensureSubscription, dropSubscription } from "./cableClient";
import { getTabId } from "../../utils/tabId";
import { getWatchlistData } from "../../api/user";
import {
  barWindowsSupportScale,
  buildLiveQuoteMoveUpdate,
  mergeBarWindowsIntoSeriesMap,
  mergeQuoteIntoSeriesMap,
} from "./liveChartQuotes";
import {
  STREAM_RENDER_STEP_MS,
  appendBotMessageText,
  buildBotMessage,
  mergeSportsCardDataIntoLatestBot,
  mergeThreadMessageIntoThreads,
  replaceBotMessageText,
  removeMessagesByIds,
  takeRenderChunk,
} from "./websocketMessageModel";

export { mergeThreadMessageIntoThreads } from "./websocketMessageModel";

// Define a function that establishes a WebSocket connection
export const connectWebSocket = (
  setQuestionOnDemand,
  current_session_id,
  websocketSubscriptionRef,
  setMessageThreads,
  showLoadingWaitingMessage,
  setShowLoadingWaitingMessage,
  useFullWidth,
  setUseFullWidth,
  setLoadingPhaseMessage,
  setLoadingPhaseDetail,
  // setDataProcessesMessage,
  pushDataProcess,
  pushAnalysisTraceEvent,
  setLiveTextOutputting,
  setLatestTickerPrices,
  setDisplayUpdatedInfo,
  setWatchlistData,
  setAnimatedItems,
  activeThread = null,
  websocketActiveChartRef,
  options = {},
) => {
  const {
    setAsPrimary = true,
    tabIdOverride = null,
    streamUserUuidOverride = null,
  } = options || {};
  const threadUuid = activeThread?.threadUuid ?? null;
  const tabId = tabIdOverride || getTabId();
  const identifier = {
    channel: "WebsocketMessageChannel",
    thread_uuid: threadUuid,
    tab_id: tabId,
  };
  if (streamUserUuidOverride) {
    identifier.stream_user_uuid = streamUserUuidOverride;
  }
  const key = JSON.stringify(identifier);  // unique per-tab+thread
  const streamRenderState = {
    activeStreamMessageId: null,
    completedStreamMessageIds: new Set(),
    pendingText: "",
    renderedText: "",
    lastSeq: 0,
    completionPending: false,
    drainTimer: null,
    destroyed: false,
    renderEpoch: 0,
  };

  const normalizeSeq = (seq) => {
    const numeric = Number(seq);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  };

  const rememberCompletedStream = (streamMessageId) => {
    if (!streamMessageId) return;
    streamRenderState.completedStreamMessageIds.add(streamMessageId);
    if (streamRenderState.completedStreamMessageIds.size > 20) {
      const oldest = streamRenderState.completedStreamMessageIds.values().next().value;
      streamRenderState.completedStreamMessageIds.delete(oldest);
    }
  };

  const isStaleStreamEvent = (streamMessageId, seq) => {
    if (!streamMessageId) return false;
    if (streamRenderState.completedStreamMessageIds.has(streamMessageId)) return true;
    if (streamRenderState.activeStreamMessageId !== streamMessageId) return false;
    return seq != null && seq <= streamRenderState.lastSeq;
  };

  const markStreamSeq = (streamMessageId, seq) => {
    if (!streamMessageId || seq == null) return;
    if (streamRenderState.activeStreamMessageId === streamMessageId) {
      streamRenderState.lastSeq = Math.max(streamRenderState.lastSeq, seq);
    }
  };

  const resetStreamAfterBranchEdit = () => {
    rememberCompletedStream(streamRenderState.activeStreamMessageId);
    clearDrainTimer();
    streamRenderState.pendingText = "";
    streamRenderState.renderedText = "";
    streamRenderState.activeStreamMessageId = null;
    streamRenderState.lastSeq = 0;
    streamRenderState.completionPending = false;
    streamRenderState.renderEpoch += 1;
    setLiveTextOutputting(false);
  };

  const clearDrainTimer = () => {
    if (!streamRenderState.drainTimer) return;
    clearTimeout(streamRenderState.drainTimer);
    streamRenderState.drainTimer = null;
  };

  const finishStreamIfIdle = () => {
    if (!streamRenderState.completionPending || streamRenderState.pendingText) {
      return;
    }
    streamRenderState.completionPending = false;
    rememberCompletedStream(streamRenderState.activeStreamMessageId);
    streamRenderState.activeStreamMessageId = null;
    streamRenderState.lastSeq = 0;
    setLiveTextOutputting(false);
    // Mark the bot bubble's text stream as done so the chart subtree (which
    // has been receiving Phase 2/3 data in the background) can finally mount.
    // Until this flag flips, chart + skeleton stay hidden so the bubble shows
    // text only while the WS is still streaming tokens.
    setMessageThreads((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      if (last?.sender !== "bot" || last?.textStreamCompleted) return prev;
      const next = prev.slice();
      next[next.length - 1] = { ...last, textStreamCompleted: true };
      return next;
    });
  };

  const drainBufferedText = (scheduledEpoch = streamRenderState.renderEpoch) => {
    streamRenderState.drainTimer = null;
    if (streamRenderState.destroyed) return;
    if (scheduledEpoch !== streamRenderState.renderEpoch) return;

    const [chunk, remaining] = takeRenderChunk(streamRenderState.pendingText);
    if (!chunk) {
      finishStreamIfIdle();
      return;
    }

    streamRenderState.pendingText = remaining;
    appendBotMessageText(setMessageThreads, chunk);
    streamRenderState.renderedText += chunk;
    if (showLoadingWaitingMessage != null) setShowLoadingWaitingMessage(null);

    if (streamRenderState.pendingText) {
      const nextEpoch = streamRenderState.renderEpoch;
      streamRenderState.drainTimer = setTimeout(
        () => drainBufferedText(nextEpoch),
        STREAM_RENDER_STEP_MS,
      );
      return;
    }

    finishStreamIfIdle();
  };

  const scheduleDrain = () => {
    if (
      streamRenderState.destroyed ||
      streamRenderState.drainTimer ||
      !streamRenderState.pendingText
    ) {
      return;
    }

    const scheduledEpoch = streamRenderState.renderEpoch;
    streamRenderState.drainTimer = setTimeout(
      () => drainBufferedText(scheduledEpoch),
      STREAM_RENDER_STEP_MS,
    );
  };

  const enqueueStreamText = (text, streamMessageId = null, seq = null) => {
    const nextText = String(text || "");
    if (!nextText) return;

    const eventSeq = normalizeSeq(seq);
    if (isStaleStreamEvent(streamMessageId, eventSeq)) return;

    if (
      streamMessageId &&
      streamRenderState.activeStreamMessageId !== streamMessageId
    ) {
      clearDrainTimer();
      streamRenderState.pendingText = "";
      streamRenderState.renderedText = "";
      streamRenderState.lastSeq = 0;
      streamRenderState.completionPending = false;
      streamRenderState.renderEpoch += 1;
    }

    if (streamMessageId) {
      streamRenderState.activeStreamMessageId = streamMessageId;
    }
    markStreamSeq(streamMessageId, eventSeq);

    setLiveTextOutputting(true);
    streamRenderState.pendingText += nextText;
    scheduleDrain();
  };

  const resetBufferedStream = ({
    text = "",
    completed = false,
    streamMessageId = null,
    seq = null,
  } = {}) => {
    const eventSeq = normalizeSeq(seq);
    if (isStaleStreamEvent(streamMessageId, eventSeq)) return;

    clearDrainTimer();
    streamRenderState.renderEpoch += 1;
    streamRenderState.pendingText = "";
    streamRenderState.renderedText = text || "";
    streamRenderState.completionPending = false;
    streamRenderState.activeStreamMessageId = streamMessageId || null;
    streamRenderState.lastSeq = eventSeq || 0;

    if (typeof text === "string" && text.length > 0) {
      replaceBotMessageText(setMessageThreads, text);
    }

    if (completed) {
      rememberCompletedStream(streamMessageId);
    }
    setLiveTextOutputting(!completed);
  };

  // Ensure one subscription per key and one consumer per tab
  const subscription = ensureSubscription(
    key,
    { url: websocketUrl, identifier },
    {
      connected() {
        this._aliveTimer && clearInterval(this._aliveTimer);
        this.perform("alive");                      // kick it once
        this._aliveTimer = setInterval(() => {
          try { this.perform("alive"); } catch (_) {}
        }, 5000);
      },
      disconnected() {
        if (this._aliveTimer) clearInterval(this._aliveTimer);
      },
      rejected() {
        if (this._aliveTimer) clearInterval(this._aliveTimer);
      },
      received(data) {
        if (data["ticker_preview"]) {
          if (typeof window !== "undefined") {
            try {
              window.dispatchEvent(
                new CustomEvent("incite:ticker-preview", {
                  detail: {
                    threadUuid,
                    preview: data["ticker_preview"],
                  },
                }),
              );
            } catch (_error) {
              // A preview event should never interrupt the chat stream.
            }
          }
          return;
        }

        if (data["audio_chunk_b64"]) {
          if (typeof window !== "undefined") {
            try {
              window.dispatchEvent(
                new CustomEvent("incite:audio-chunk", {
                  detail: {
                    threadUuid,
                    audioChunkB64: data["audio_chunk_b64"],
                    audioFormat: data["audio_format"] || "mp3",
                    audioSeq: data["audio_seq"] ?? null,
                  },
                }),
              );
            } catch {}
          }
          return;
        }

        if (data["thread_resolved"]) {
          const payload = data["thread_resolved"] || {};
          const resolvedThreadUuid =
            payload.threadUuid || payload.thread_uuid || null;
          const tempThreadUuid =
            payload.tempThreadUuid || payload.temp_thread_uuid || threadUuid;
          if (
            resolvedThreadUuid &&
            tempThreadUuid &&
            typeof window !== "undefined"
          ) {
            try {
              window.dispatchEvent(
                new CustomEvent("incite:thread-resolved", {
                  detail: {
                    tempThreadUuid,
                    threadUuid: resolvedThreadUuid,
                    threadTitle:
                      payload.threadTitle ||
                      payload.thread_title ||
                      payload.title ||
                      "Chat",
                  },
                }),
              );
            } catch {}
          }
        }

        // This is JUST for the loading phases that appear below the chat
        if (data["loading_phase"]) {
          setLoadingPhaseMessage(data["loading_phase"]);
          if (
            !data["stream_event"] &&
            !Object.prototype.hasOwnProperty.call(data, "loading_phase_detail") &&
            !Object.prototype.hasOwnProperty.call(data, "message") &&
            !Object.prototype.hasOwnProperty.call(data, "data_processes_snapshot")
          ) {
            return;
          }
        }

        if (Object.prototype.hasOwnProperty.call(data, "loading_phase_detail")) {
          setLoadingPhaseDetail(data["loading_phase_detail"]);
          if (
            !data["stream_event"] &&
            !Object.prototype.hasOwnProperty.call(data, "message") &&
            !Object.prototype.hasOwnProperty.call(data, "data_processes_snapshot")
          ) {
            return;
          }
        }

        if (Array.isArray(data["data_processes_snapshot"])) {
          data["data_processes_snapshot"].forEach((event) => {
            if (event?.message) pushDataProcess(event);
          });
        }

        if (data["analysis_trace_event"]) {
          pushAnalysisTraceEvent?.(data["analysis_trace_event"]);
          return;
        }

        // If there was an agentic watchlist reload
        if (data["watchlist_reload"]) {
          getWatchlistData(setWatchlistData);
          return;
        }

        // This is for the data process that will appear in the side bar.
        if (data["data_processes"]) {
          // setDataProcessesMessage(data["data_processes"]);
          // This had to become a queue because how how many are set at once!! otherwise replaces and not all will show on side data processes panel
          pushDataProcess({
            message: data["data_processes"],
            require_completion: data["require_completion"],
            stage_key: data["stage_key"],
            evidence_items: data["evidence_items"],
          });
          return;
        }

        // used to mark compelete (if state requires it)
        if (data["data_processes_complete"]) {
          pushDataProcess({
            message: data["data_processes_complete"],
            complete: true,
            stage_key: data["stage_key"],
            evidence_items: data["evidence_items"],
          });
          return;
        }

        // Phase 3-extension: backend broadcasts follow-up questions as soon
        // as the parallel LLM thread finishes (instead of waiting for the
        // HTTP response). Forward to ChatPage via a window event; ChatPage
        // is the owner of responseFlow / setResponseFlowForThread which is
        // where the prepop buttons live.
        if (data["questions_prepop"]) {
          const raw = data["questions_prepop"];
          if (Array.isArray(raw)) {
            const cleaned = raw
              .map((q) => (typeof q === "string" ? q : null))
              .filter((q) => q && q.trim());
            if (cleaned.length > 0 && typeof window !== "undefined") {
              try {
                window.dispatchEvent(
                  new CustomEvent("incite:questions-prepop", {
                    detail: { threadUuid, questions: cleaned },
                  }),
                );
              } catch {}
            }
          }
          return;
        }

        // Early chart-shape hint over WS so the row can reserve layout space
        // (skeleton sized correctly) before the full chart payload arrives in
        // the HTTP response. The skeleton clears once answeredTickerIds and
        // tickerPrices land via the completion payload.
        if (data["chart_hint"]) {
          const hint = data["chart_hint"] || {};
          const tickerIds = Array.isArray(hint.ticker_ids)
            ? hint.ticker_ids.map((v) => String(v))
            : Array.isArray(hint.tickerIds)
              ? hint.tickerIds.map((v) => String(v))
              : [];
          const tickers = Array.isArray(hint.tickers)
            ? hint.tickers.map((v) => String(v))
            : [];
          const logoUrls = Array.isArray(hint.logo_urls)
            ? hint.logo_urls
            : Array.isArray(hint.logoUrls)
              ? hint.logoUrls
              : [];
          // The pre-LLM optimistic broadcast tags itself `optimistic: true` and
          // fires for any ticker in the question, regardless of the user's
          // show-charts preference or whether the answer ends up charting. The
          // authoritative hint (emitted only when the answer references tickers)
          // carries no flag. Record that distinction so the client can recover a
          // stuck skeleton only when a chart was actually intended.
          const isAuthoritativeHint = hint.optimistic !== true;
          if (tickerIds.length > 0) {
            setMessageThreads((prev) => {
              const last = prev.length ? prev[prev.length - 1] : null;
              // Phase 3: the optimistic broadcast can fire BEFORE the text
              // stream creates the bot bubble (lastSender is still 'human').
              // Push an empty bot placeholder so the hint isn't dropped;
              // appendBotMessageText will see the existing bot bubble and
              // append text into it when the stream starts.
              if (!last || last.sender !== "bot") {
                return [
                  ...prev,
                  {
                    ...buildBotMessage(""),
                    pendingChartTickerIds: tickerIds,
                    pendingChartTickers: tickers,
                    pendingChartLogoUrls: logoUrls,
                    chartAuthoritative: isAuthoritativeHint,
                  },
                ];
              }
              if (last.completed) return prev;
              const next = prev.slice();
              next[next.length - 1] = {
                ...last,
                pendingChartTickerIds: tickerIds,
                pendingChartTickers: tickers,
                pendingChartLogoUrls: logoUrls,
                chartAuthoritative:
                  last.chartAuthoritative || isAuthoritativeHint,
              };
              return next;
            });
          }
          return;
        }

        // Phase 2/3: recent daily bars for the primary ticker. The Phase 3
        // optimistic broadcast (fired before the text LLM) and the Phase 2
        // post-text broadcast share the same shape; a late "correction"
        // simply overwrites the optimistic primary. Multi-ticker hints from
        // chart_hint are preserved by rebuilding the map with primary first.
        if (data["chart_data_recent"]) {
          const payload = data["chart_data_recent"] || {};
          const tickerId =
            payload.ticker_id != null
              ? String(payload.ticker_id)
              : payload.tickerId != null
                ? String(payload.tickerId)
                : null;
          const ticker =
            typeof payload.ticker === "string" ? payload.ticker : null;
          const tickerDescription =
            typeof payload.ticker_description === "string"
              ? payload.ticker_description
              : typeof payload.tickerDescription === "string"
                ? payload.tickerDescription
                : null;
          const tickerPrices = Array.isArray(payload.ticker_prices)
            ? payload.ticker_prices
            : Array.isArray(payload.tickerPrices)
              ? payload.tickerPrices
              : null;
          // Price metadata for the DataCluster/DisplayData panel above the
          // chart — lets it render fully populated alongside the bars instead
          // of waiting for the HTTP response.
          const currentPrice =
            payload.current_price ?? payload.currentPrice ?? null;
          const closePrice =
            payload.close_price ?? payload.closePrice ?? null;
          const currentPriceUpdatedAt =
            payload.current_price_updated_at ??
            payload.currentPriceUpdatedAt ??
            null;
          const assetType =
            typeof payload.asset_type === "string"
              ? payload.asset_type
              : typeof payload.assetType === "string"
                ? payload.assetType
                : null;
          const pricePctChange1d =
            payload.price_pct_change_1d ?? payload.pricePctChange1d ?? null;
          const pricePctChangeAh =
            payload.price_pct_change_ah ?? payload.pricePctChangeAh ?? null;
          if (tickerId && Array.isArray(tickerPrices) && tickerPrices.length > 0) {
            setMessageThreads((prev) => {
              const last = prev.length ? prev[prev.length - 1] : null;

              const hintIds = Array.isArray(last?.pendingChartTickerIds)
                ? last.pendingChartTickerIds
                : [];
              const hintTickers = Array.isArray(last?.pendingChartTickers)
                ? last.pendingChartTickers
                : [];
              const tickerFor = (id) => {
                const idx = hintIds.indexOf(id);
                if (idx >= 0 && hintTickers[idx]) return String(hintTickers[idx]);
                if (id === tickerId && ticker) return ticker;
                return "";
              };
              const otherIds = hintIds.filter((id) => id !== tickerId);
              const nextIds = [tickerId, ...otherIds];
              const nextMap = nextIds.map((id) => ({
                id,
                ticker: tickerFor(id),
              }));

              // tickerDescription drives the asset-name title + action
              // toolbar. Position primary's description first; preserve
              // any later descriptions from a prior chart_hint pass.
              const existingDescriptions = Array.isArray(last?.tickerDescription)
                ? last.tickerDescription
                : [];
              const nextDescriptions = tickerDescription
                ? [String(tickerDescription), ...existingDescriptions.slice(1)]
                : existingDescriptions;

              // Phase 3: like chart_hint, this can fire before the bot bubble
              // exists. Push a placeholder so the chart can mount immediately.
              if (!last || last.sender !== "bot") {
                return [
                  ...prev,
                  {
                    ...buildBotMessage(""),
                    answeredTickerIds: nextIds,
                    answeredTickerMap: nextMap,
                    ticker: ticker ?? "",
                    tickerDescription: nextDescriptions,
                    tickerPrices,
                    currentPrice: currentPrice ?? "",
                    closePrice: closePrice ?? "",
                    currentPriceUpdatedAt: currentPriceUpdatedAt ?? "",
                    assetType: assetType ?? "",
                    pricePctChange1d:
                      pricePctChange1d != null ? String(pricePctChange1d) : "",
                    pricePctChangeAh:
                      pricePctChangeAh != null ? String(pricePctChangeAh) : "",
                  },
                ];
              }
              if (last.completed) return prev;

              const next = prev.slice();
              next[next.length - 1] = {
                ...last,
                answeredTickerIds: nextIds,
                answeredTickerMap: nextMap,
                ticker: ticker ?? last.ticker,
                tickerDescription: nextDescriptions,
                tickerPrices,
                currentPrice:
                  currentPrice != null ? currentPrice : last.currentPrice,
                closePrice:
                  closePrice != null ? closePrice : last.closePrice,
                currentPriceUpdatedAt:
                  currentPriceUpdatedAt != null
                    ? currentPriceUpdatedAt
                    : last.currentPriceUpdatedAt,
                assetType:
                  assetType != null ? assetType : last.assetType,
                pricePctChange1d:
                  pricePctChange1d != null
                    ? String(pricePctChange1d)
                    : last.pricePctChange1d,
                pricePctChangeAh:
                  pricePctChangeAh != null
                    ? String(pricePctChangeAh)
                    : last.pricePctChangeAh,
              };
              return next;
            });
          }
          return;
        }

        // Trade proposal captured during answer parsing, broadcast ahead of the
        // HTTP response. Same shape as refinedQueryObj.trade_proposed, so the
        // ActionBox and chart overlays render from it unchanged; the HTTP
        // response later reconciles with the authoritative copy.
        if (data["trade_proposed"]) {
          const proposal = data["trade_proposed"];
          if (
            proposal &&
            typeof proposal === "object" &&
            Object.keys(proposal).length > 0
          ) {
            setMessageThreads((prev) => {
              const last = prev.length ? prev[prev.length - 1] : null;
              if (!last || last.sender !== "bot") {
                return [
                  ...prev,
                  {
                    ...buildBotMessage(""),
                    refinedQueryObj: { trade_proposed: proposal },
                  },
                ];
              }
              if (last.completed) return prev;
              const next = prev.slice();
              next[next.length - 1] = {
                ...last,
                refinedQueryObj: {
                  ...(last.refinedQueryObj || {}),
                  trade_proposed: proposal,
                },
              };
              return next;
            });
          }
          return;
        }

        if (data["sports_card_data"] || data["sportsCardData"]) {
          const sportsCardData = data["sports_card_data"] || data["sportsCardData"];
          setMessageThreads((prev) =>
            mergeSportsCardDataIntoLatestBot(prev, sportsCardData),
          );
          return;
        }

        if (data["on-demand"]) {
          setTimeout(() => setQuestionOnDemand(true), 500);
          return;
        }
        if (data["overwrite-question"]) {
          // IMPORTANT: never mutate state in place
          setMessageThreads(prev => {
            if (!prev.length) return prev;
            const next = prev.slice();
            next[next.length - 1] = {
              ...next[next.length - 1],
              message: data["overwrite-question"],
            };
            return next;
          });
          return;
        }
        if (data["price-websocket-data"]) {
          const wd = data["price-websocket-data"];
          handlePriceData(
            wd,
            setLatestTickerPrices,
            setDisplayUpdatedInfo,
            websocketActiveChartRef
          );
          return;
        }

        if (data["watchlist-websocket-data"]) {
          handleWebsocketData(data["watchlist-websocket-data"], setWatchlistData, setAnimatedItems)
          return;
        }

        if (data.thread_messages_deleted) {
          const payload = data.thread_messages_deleted || {};
          const messageIds = payload.messageIds || payload.message_ids || [];
          resetStreamAfterBranchEdit();
          setMessageThreads((prev) => removeMessagesByIds(prev, messageIds));
          return;
        }

        if (data["stream_event"]) {
          const evt = data["stream_event"] || {};
          const eventType = evt["type"];
          const text = typeof evt["text"] === "string" ? evt["text"] : "";
          const fullText =
            typeof evt["fullText"] === "string"
              ? evt["fullText"]
              : typeof evt["full_text"] === "string"
                ? evt["full_text"]
                : null;
          const completed = evt["completed"] === true;
          const streamMessageId =
            evt["streamMessageId"] == null ? null : String(evt["streamMessageId"]);
          const eventSeq = normalizeSeq(evt["seq"]);

          if (eventType === "snapshot") {
            resetBufferedStream({
              text: fullText ?? text,
              completed,
              streamMessageId,
              seq: eventSeq,
            });
            return;
          }

          if (eventType === "delta") {
            enqueueStreamText(text, streamMessageId, eventSeq);
            return;
          }

          if (eventType === "completed") {
            if (isStaleStreamEvent(streamMessageId, eventSeq)) return;
            if (typeof fullText === "string") {
              const knownText = `${streamRenderState.renderedText}${streamRenderState.pendingText}`;
              if (fullText.startsWith(knownText)) {
                const missingText = fullText.slice(knownText.length);
                if (missingText) {
                  enqueueStreamText(missingText, streamMessageId, eventSeq);
                }
              } else if (!knownText) {
                enqueueStreamText(fullText, streamMessageId, eventSeq);
              }
            }
            markStreamSeq(streamMessageId, eventSeq);
            streamRenderState.completionPending = true;
            if (streamMessageId) {
              streamRenderState.activeStreamMessageId = streamMessageId;
            }
            finishStreamIfIdle();
            return;
          }
        }

        if (!Object.prototype.hasOwnProperty.call(data, "message")) return;

        enqueueStreamText(data["message"]);
        // if (!useFullWidth) setUseFullWidth(true);
      },
    }
  );

  if (setAsPrimary !== false && websocketSubscriptionRef) {
    websocketSubscriptionRef.current = subscription;
  }

  // Let caller clean up by key
  return {
    key,
    subscription,
    cleanup: () => {
      streamRenderState.destroyed = true;
      clearDrainTimer();
      dropSubscription(key, "thread cleanup");
    },
  };
};

export const connectUserThreadUpdates = ({
  setThreads,
  setMessageThreads,
  activeThreadRef,
  setActiveThread,
  markThreadUnread,
  markThreadRead,
  getLastReadAt,
  getLastReadBotId,
  onPendingSignupQuestion,
} = {}) => {
  const callbacks = {
      connected() {
      },
      disconnected() {
      },
      rejected() {
      },
      received(data) {
        if (!data) return;

        if (data.pending_signup_question) {
          onPendingSignupQuestion?.(data.pending_signup_question);
        }

        if (typeof window !== "undefined") {
          if (data.user_notification) {
            window.dispatchEvent(
              new CustomEvent("user-notifications:updated", {
                detail: { notification: data.user_notification },
              }),
            );
          }

          if (data.trade_proposal_event) {
            window.dispatchEvent(
              new CustomEvent("trade-proposals:event", {
                detail: data.trade_proposal_event,
              }),
            );
            window.dispatchEvent(new CustomEvent("trade-proposals:updated"));
          }
        }

        if (data.thread_created) {
          const t = data.thread_created;
          if (!t?.threadUuid) return;
          setThreads((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            const next = [
              {
                ...t,
                threadUuid: t.threadUuid,
                title: t.title || "Chat",
                isAdminTargeted: Boolean(t.isAdminTargeted),
                pairedThreadId: t.pairedThreadId ?? t.paired_thread_id,
                pairedUserId: t.pairedUserId ?? t.paired_user_id,
                pairedUserUsername:
                  t.pairedUserUsername ?? t.paired_user_username,
              },
              ...safePrev.filter((row) => row.threadUuid !== t.threadUuid),
            ];
            return next;
          });
          if (activeThreadRef?.current?.threadUuid !== t.threadUuid) {
            if (typeof markThreadUnread === "function") {
              markThreadUnread(t.threadUuid);
            }
          }
          return;
        }

        if (data.thread_message) {
          const payload = data.thread_message;
          const threadUuid = payload?.threadUuid;
          if (!threadUuid) return;

          const msg = payload?.message;
          const userMsg = payload?.userMessage ?? payload?.user_message;
          const isActive = activeThreadRef?.current?.threadUuid === threadUuid;
          setThreads((prev) => mergeThreadMessageIntoThreads(prev, payload));

          if (
            msg &&
            activeThreadRef?.current?.threadUuid === threadUuid &&
            typeof setMessageThreads === "function"
          ) {
            // Question and answer land in one state update so they paint on
            // the same frame; pushedEntrance drives the one-time fade-in.
            setMessageThreads((prev) => {
              const safePrev = Array.isArray(prev) ? prev : [];
              const incoming = [userMsg, msg]
                .filter(Boolean)
                .filter(
                  (m) => !safePrev.some((existing) => existing.id === m.id),
                )
                .map((m) => ({ ...m, pushedEntrance: true }));
              return incoming.length ? [...safePrev, ...incoming] : safePrev;
            });
            if (typeof markThreadRead === "function") {
              markThreadRead(threadUuid);
            }
          } else if (!isActive) {
            const isBot = msg?.sender === "bot";
            if (isBot && typeof markThreadUnread === "function") {
              const msgId = Number(msg?.id);
              const lastReadBotId =
                typeof getLastReadBotId === "function"
                  ? Number(getLastReadBotId(threadUuid))
                  : NaN;
              if (
                Number.isFinite(msgId) &&
                Number.isFinite(lastReadBotId) &&
                msgId <= lastReadBotId
              ) {
                return;
              }
              const rawTime = msg?.createdAt || payload?.updatedAt || null;
              const msgTime = rawTime ? Date.parse(rawTime) : null;
              const lastReadAt =
                typeof getLastReadAt === "function"
                  ? getLastReadAt(threadUuid)
                  : null;
              if (lastReadAt && msgTime && msgTime <= lastReadAt) {
                return;
              }
              markThreadUnread(threadUuid);
            }
          }
        }

        if (data.thread_deleted) {
          const threadUuid = data.thread_deleted?.threadUuid;
          if (!threadUuid) return;
          setThreads((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            return safePrev.filter((row) => row.threadUuid !== threadUuid);
          });

          if (activeThreadRef?.current?.threadUuid === threadUuid) {
            if (typeof setActiveThread === "function") setActiveThread(null);
            if (typeof setMessageThreads === "function") setMessageThreads([]);
          }
          if (typeof markThreadRead === "function") {
            markThreadRead(threadUuid);
          }
        }
      },
  };

  // Two subscriptions, one handler. `user` carries per-device events
  // (notifications, thread created/deleted, reengagement pushes); `account`
  // carries cross-device chat sync, published once per account instead of once
  // per device. The handler already dispatches on payload key, so it serves
  // both without caring which stream a frame arrived on.
  ["user", "account"].forEach((scope) => {
    const identifier = { channel: "WebsocketMessageChannel", scope };
    ensureSubscription(
      JSON.stringify(identifier),
      { url: websocketUrl, identifier },
      callbacks,
    );
  });

  // Deliberately NOT dropSubscription here. The caller's effect re-runs on
  // unstable deps, and unsubscribing/resubscribing the same identifier in
  // quick succession desyncs ActionCable: the client keeps a "confirmed"
  // subscription object while the server-side subscription is gone, so
  // user-scope broadcasts silently stop arriving. ensureSubscription swaps
  // the callbacks in place on the next call, so the handlers stay fresh
  // without teardown; the subscription is torn down only on identity change
  // (syncCableIdentity → disconnectAll).
  return () => {};
};

export const handlePriceData = (
  websocketData,
  setLatestTickerPrices,
  setDisplayUpdatedInfo,
  websocketActiveChartRef
) => {
  if (!websocketData) return;

  const priceData = websocketData["prices-values"];
  const pctChangeData = websocketData["pct-change"];
  const priceChangeData = websocketData["price-change"];
  const strPriceData = websocketData["prices"];
  const updatedAtData = websocketData["updated-at"];
  const updatedAtIsoData = websocketData["updated-at-iso"];
  const barWindowsData = websocketData["bar-windows"];

  if (!priceData) return;

  const payloadIdx =
    websocketData?.idx == null ? null : String(websocketData.idx);
  const activeChartState = websocketActiveChartRef?.current || {};
  const activeChart =
    (payloadIdx && activeChartState?.chartsByIdx?.[payloadIdx]) ||
    activeChartState;
  const activeIdx = activeChart?.idx == null ? null : String(activeChart.idx);
  const ids = Object.keys(priceData || {});
  if (ids.length === 0) return;
  const activeTickerId =
    activeChart?.tickerId == null ? null : String(activeChart.tickerId);
  const targetIdxs = new Set();

  if (payloadIdx) {
    targetIdxs.add(payloadIdx);
  }

  if (
    activeIdx &&
    activeTickerId &&
    ids.includes(activeTickerId)
  ) {
    targetIdxs.add(activeIdx);
  }

 
  setDisplayUpdatedInfo((prevInfo) => {
    const nextInfo = { ...(prevInfo || {}) };
    ids.forEach((tickerId) => {
      const nextPrice = Number(priceData[tickerId]);
      if (!Number.isFinite(nextPrice)) return;

      const numericId = Number(tickerId);
      if (!Number.isFinite(numericId)) return;

      const formattedPrice = strPriceData?.[tickerId];
      const formattedTime = updatedAtData?.[tickerId];
      const updatedAtIso = updatedAtIsoData?.[tickerId];
      const pct1d = pctChangeData?.[tickerId];
      const priceChange = priceChangeData?.[tickerId];
      const currentInfo = nextInfo[numericId] || {};
      const quoteMoveFields = buildLiveQuoteMoveUpdate({
        currentInfo,
        nextPrice,
        quoteAtIso: updatedAtIso,
        assetType: currentInfo.assetType || activeChart.assetType,
        pctChangeValue: pct1d,
        priceChangeValue: priceChange,
        includeNullFields: true,
      });

      nextInfo[numericId] = {
        ...currentInfo,
        currentPrice:
          formattedPrice != null ? formattedPrice : currentInfo.currentPrice,
        currentPriceValue: nextPrice,
        currentPriceUpdatedAt:
          formattedTime != null
            ? formattedTime
            : currentInfo.currentPriceUpdatedAt,
        currentPriceUpdatedAtIso:
          updatedAtIso != null
            ? updatedAtIso
            : currentInfo.currentPriceUpdatedAtIso,
        ...quoteMoveFields,
      };
    });
    return nextInfo;
  });

  if (targetIdxs.size > 0 && typeof window !== "undefined") {
    try {
      const quotesByTickerId = {};
      ids.forEach((tickerId) => {
        const nextPrice = Number(priceData[tickerId]);
        if (!Number.isFinite(nextPrice)) return;
        const updatedAtIso = updatedAtIsoData?.[tickerId];
        const pctChangeValue =
          pctChangeData?.[tickerId] != null ? pctChangeData[tickerId] : null;
        const priceChangeValue =
          priceChangeData?.[tickerId] != null ? priceChangeData[tickerId] : null;
        const quoteMoveFields = buildLiveQuoteMoveUpdate({
          currentInfo: {},
          nextPrice,
          quoteAtIso: updatedAtIso,
          assetType: activeChart.assetType,
          pctChangeValue,
          priceChangeValue,
        });

        const quote = {
          currentPrice:
            strPriceData?.[tickerId] != null ? strPriceData[tickerId] : null,
          currentPriceValue: nextPrice,
          currentPriceUpdatedAt:
            updatedAtData?.[tickerId] != null ? updatedAtData[tickerId] : null,
          currentPriceUpdatedAtIso:
            updatedAtIso != null ? updatedAtIso : null,
          ...quoteMoveFields,
        };
        quotesByTickerId[String(tickerId)] = quote;
      });

      if (Object.keys(quotesByTickerId).length > 0) {
        targetIdxs.forEach((targetIdx) => {
          window.dispatchEvent(
            new CustomEvent("incite:chat-live-quote", {
              detail: {
                idx: targetIdx,
                quotesByTickerId,
              },
            }),
          );
        });
      }
    } catch (_error) {}
  }

  if (targetIdxs.size === 0) return;

  setLatestTickerPrices((prevPrices) => {
    let nextPrices = prevPrices;

    ids.forEach((tickerId) => {
      const nextPrice = Number(priceData[tickerId]);
      const updatedAtIso = updatedAtIsoData?.[tickerId];
      if (!Number.isFinite(nextPrice) || !updatedAtIso) return;

      targetIdxs.forEach((targetIdx) => {
        const barWindows = barWindowsData?.[tickerId];
        if (
          Array.isArray(barWindows) &&
          barWindows.length > 0 &&
          barWindowsSupportScale({
            scale: activeChart?.scale,
            windows: barWindows,
          })
        ) {
          nextPrices = mergeBarWindowsIntoSeriesMap({
            seriesMap: nextPrices,
            tickerId,
            idx: targetIdx,
            windows: barWindows,
            assetType: activeChart.assetType,
          });
        } else {
          nextPrices = mergeQuoteIntoSeriesMap({
            seriesMap: nextPrices,
            tickerId,
            idx: targetIdx,
            price: nextPrice,
            quoteAtIso: updatedAtIso,
            assetType: activeChart.assetType,
          });
        }
      });
    });

    return nextPrices;
  });
};

const handleWebsocketData = (websocketData, setWatchlistData, setAnimatedItems) => {
  if (!websocketData) return

  const priceData     = websocketData["prices"];      // stringified/pretty price
  const pctChangeData = websocketData["pct-change"];  // {}
  if (!priceData) return;

  setWatchlistData(prev => {
    // Build new arrays without mutating prev
    const bump = (oldPrice, newPrice) => (parseFloat(newPrice) > parseFloat(oldPrice) ? "green" : "red");

    const mapBlock = (list) => list.map(item => {
      const p = priceData[item.id];
      if (!p) return item;

      if (String(item.currentPrice) !== String(p)) {
        setAnimatedItems(prevA => ({ ...prevA, [item.id]: bump(item.currentPrice, p) }));
      }
      const addPct = pctChangeData[item.id] === "" ? {} : { pricePctChange1d: pctChangeData[item.id] };
      return { ...item, currentPrice: p, ...addPct };
    });

    const updated = {
      Stocks:      mapBlock(prev.Stocks),
      Cryptos:     mapBlock(prev.Cryptos),
      ForexPairs:  mapBlock(prev.ForexPairs),
      Indices:     mapBlock(prev.Indices),
      Commodities: mapBlock(prev.Commodities),
    };

    // Flash color *before* the numbers flip
    setTimeout(() => setWatchlistData(() => updated), 300);

    // Return previous now; we’ll apply `updated` after delay
    return prev;
  });

}
